import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';
import {
  Users, Map, Network, Target, Layers,
  Upload, X, Play, ChevronRight, Crown,
  FileText, Copy, Loader2
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MD_TABLE_COMPONENTS } from '../components/Common/MarkdownContent';

// ─── Tool Definitions ─────────────────────────────────────────────────────────

interface StrategicTool {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  color: string;
  gradient: string;
  placeholder: string;
  inputLabel: string;
}

const TOOLS: StrategicTool[] = [
  {
    id: 'user-research-synthesis',
    name: 'User Research Synthesis',
    icon: Users,
    color: '#6366f1',
    gradient: 'from-indigo-50 to-white',
    description: 'Synthesize stakeholder interviews, transcripts, surveys, or raw data into structured insights, themes, pain points, and prioritized recommendations.',
    inputLabel: 'Paste your research data, interview transcripts, survey results, or describe the research context',
    placeholder: `Example:
- We conducted 12 stakeholder interviews across Finance, Operations, and IT
- Key question: "What are the biggest friction points in the current P2P process?"
- Key themes emerging: manual approval bottlenecks, lack of visibility into PO status, duplicate invoice issues...

Or paste raw interview transcripts / survey data directly.`,
  },
  {
    id: 'roadmap-narrative',
    name: 'Roadmap Narrative',
    icon: Map,
    color: '#f59e0b',
    gradient: 'from-amber-50 to-white',
    description: 'Transform a prioritized initiative list into a compelling strategic roadmap narrative for executives and operational teams.',
    inputLabel: 'List your prioritized initiatives, OKRs, and strategic context',
    placeholder: `Example:
Company OKRs for FY2025:
- Reduce operational costs by 20%
- Improve client NPS from 42 to 65
- Launch 3 new product lines in APAC

Prioritized initiatives:
1. Automate AP/AR reconciliation (HIGH)
2. Migrate legacy ERP to Workday (HIGH)
3. Launch client self-service portal (MEDIUM)
4. Implement real-time analytics dashboard (MEDIUM)
5. Expand vendor management module (LOW)`,
  },
  {
    id: 'stakeholder-influence-mapper',
    name: 'Stakeholder Influence Mapper',
    icon: Network,
    color: '#10b981',
    gradient: 'from-emerald-50 to-white',
    description: 'Build stakeholder maps, influence strategies, and tailored talking points to secure alignment, buy-in, and navigate organizational change.',
    inputLabel: 'Describe the initiative, key stakeholders, and their known concerns or stances',
    placeholder: `Example:
Initiative: Implementing a new vendor management platform across 3 business units.

Key stakeholders:
- CFO (Sarah M.) — concerned about cost, ROI timeline
- CTO (James L.) — supportive, wants API-first solution
- VP Operations (Tom R.) — skeptical, worried about change disruption to his team
- Legal (Maria C.) — unknown stance, needs compliance assurance
- Procurement Manager (Alex B.) — champion, has been asking for this for 2 years`,
  },
  {
    id: 'okr-cascade-generator',
    name: 'OKR Cascade Generator',
    icon: Target,
    color: '#8b5cf6',
    gradient: 'from-violet-50 to-white',
    description: 'Automate company-to-team OKR cascading with alignment scoring, data feed definitions, and metrics for strategic initiatives.',
    inputLabel: 'Provide your company-level OKRs and the teams/products that need to cascade them',
    placeholder: `Example:
Company OKRs FY2025 Q2:
- O1: Become the market leader in mid-market RCSA solutions
  - KR1: Increase MRR by 40% to $2.4M by June 30
  - KR2: Achieve NPS > 70 across enterprise clients
  - KR3: Launch 2 new integrations (Workday, Salesforce)

Teams to cascade to:
- Product team
- Engineering team
- Sales team
- Customer Success team`,
  },
  {
    id: 'change-management-suite',
    name: 'Change Management Suite',
    icon: Layers,
    color: '#ef4444',
    gradient: 'from-red-50 to-white',
    description: 'Full suite: change impact assessment, board deck structure, scenario war room, and governance playbook for complex programs.',
    inputLabel: 'Describe the change initiative, its scope, timeline, and organizational context',
    placeholder: `Example:
Initiative: Migration from legacy on-premise ERP to Workday cloud platform.

Scope: 450 employees across Finance, HR, and Operations in 3 offices (NY, London, Singapore).
Timeline: 18-month phased rollout starting Q3 2025.
Driver: Board mandate to reduce IT infrastructure costs by 30% and improve real-time reporting.
Key risks: Data migration complexity, user adoption, business continuity during cutover.
Stakeholder pressure: CFO wants board update in 6 weeks.`,
  },
];

// ─── Result Interface ─────────────────────────────────────────────────────────
interface ToolResult {
  analysis: string;
  tool: { id: string; name: string };
  hasFile: boolean;
  fileName: string | null;
}

// ─── Main Component ───────────────────────────────────────────────────────────
const StrategicTools: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedTool, setSelectedTool] = useState<StrategicTool>(TOOLS[0]);
  const [prompt, setPrompt] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ToolResult | null>(null);

  const handleToolSelect = (tool: StrategicTool) => {
    setSelectedTool(tool);
    setPrompt('');
    setFiles([]);
    setResult(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const removeFile = (idx: number) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleRun = async () => {
    if (!selectedTool || !prompt.trim()) {
      toast.error('Please enter your input before running.');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('mode', selectedTool.id);
      formData.append('prompt', prompt.trim());
      files.forEach(f => formData.append('files', f));

      const response = await api.post('/api/intelligence/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        const d = response.data.data;
        setResult({ ...d, tool: d.tool || d.mode });
        toast.success(`${selectedTool.name} complete!`);
      } else {
        toast.error(response.data.error || 'Something went wrong');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to run tool');
    } finally {
      setLoading(false);
    }
  };

  const handleSendToCouncil = () => {
    if (!result) return;
    const councilPrompt = `Review and critique this ${result.tool.name} output. Identify gaps, challenge assumptions, and provide a multi-perspective evaluation:`;
    const inputData = result.analysis.slice(0, 8000);
    navigate('/app/ai-council', { state: { prompt: councilPrompt, inputData } });
    toast.success('Sent to AI Council!');
  };

  const handleCopyResult = () => {
    if (result?.analysis) {
      navigator.clipboard.writeText(result.analysis);
      toast.success('Copied to clipboard');
    }
  };

  return (
    <div className="flex h-full bg-gray-50 overflow-hidden">

      {/* ── Left Panel: Tool List ─────────────────────────────── */}
      <div className="w-72 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col h-full">
        <div className="px-5 py-5 border-b border-gray-100">
          <div className="flex items-center space-x-2 mb-1">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#221551] to-[#57C8F4] flex items-center justify-center">
              <Layers className="w-3.5 h-3.5 text-white" />
            </div>
            <h1 className="text-base font-bold text-gray-900">Strategic Tools</h1>
          </div>
          <p className="text-xs text-gray-400 leading-relaxed mt-1">
            Consulting-grade AI tools. Outputs can be sent to AI Council for deeper analysis.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 pb-2">Select Tool</p>
          {TOOLS.map(tool => {
            const Icon = tool.icon;
            const isActive = selectedTool.id === tool.id;
            return (
              <button
                key={tool.id}
                onClick={() => handleToolSelect(tool)}
                className={`w-full text-left px-3 py-3 rounded-xl transition-all duration-150 border ${
                  isActive ? 'bg-white shadow-sm' : 'border-transparent hover:bg-gray-50'
                }`}
                style={isActive ? { borderColor: tool.color + '50' } : {}}
              >
                <div className="flex items-start space-x-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: tool.color + '18' }}
                  >
                    <Icon className="w-4 h-4" style={{ color: tool.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium leading-tight ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                      {tool.name}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5 line-clamp-2 leading-relaxed">
                      {tool.description}
                    </div>
                  </div>
                  {isActive && <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 mt-1" style={{ color: tool.color }} />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Right Panel: Input + Output ───────────────────────── */}
      <div className="flex-1 overflow-y-auto p-8 space-y-6">

        {/* Tool Input Card */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
          <div
            className="px-6 py-4 border-b"
            style={{ background: `linear-gradient(to right, ${selectedTool.color}12, white)`, borderColor: selectedTool.color + '25' }}
          >
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: selectedTool.color + '18' }}>
                <selectedTool.icon className="w-5 h-5" style={{ color: selectedTool.color }} />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">{selectedTool.name}</h2>
                <p className="text-xs text-gray-500">{selectedTool.description}</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{selectedTool.inputLabel}</label>
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder={selectedTool.placeholder}
                rows={9}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 resize-none bg-gray-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Supporting Documents <span className="text-gray-400 font-normal text-xs">(optional — PDF, DOCX, TXT)</span>
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-gray-300 hover:bg-gray-50 transition-all"
              >
                <Upload className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                <p className="text-xs text-gray-500">Click to upload supporting documents</p>
              </div>
              <input ref={fileInputRef} type="file" multiple accept=".pdf,.docx,.doc,.txt" onChange={handleFileChange} className="hidden" />
              {files.length > 0 && (
                <div className="mt-2 space-y-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                      <div className="flex items-center space-x-2">
                        <FileText className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-xs text-gray-700">{f.name}</span>
                      </div>
                      <button onClick={() => removeFile(i)}><X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={handleRun}
              disabled={loading || !prompt.trim()}
              className="w-full flex items-center justify-center space-x-2 py-3 rounded-xl font-semibold text-white text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              style={{ backgroundColor: selectedTool.color }}
            >
              {loading
                ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Running {selectedTool.name}...</span></>
                : <><Play className="w-4 h-4" /><span>Run {selectedTool.name}</span></>
              }
            </button>
          </div>
        </div>

        {/* Output Card */}
        {result && (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: selectedTool.color }} />
                <span className="font-semibold text-gray-800 text-sm">{result.tool.name} — Output</span>
                {result.hasFile && (
                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{result.fileName}</span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleCopyResult}
                  className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 transition-colors"
                >
                  <Copy className="w-3 h-3" /><span>Copy</span>
                </button>
                <button
                  onClick={handleSendToCouncil}
                  className="flex items-center space-x-1 text-xs text-white rounded-lg px-3 py-1.5 font-medium"
                  style={{ backgroundColor: '#221551' }}
                >
                  <Crown className="w-3 h-3" /><span>Send to AI Council</span>
                </button>
              </div>
            </div>
            <div className="p-6 prose prose-sm max-w-none prose-headings:text-gray-800 prose-headings:font-semibold prose-table:text-xs overflow-auto">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{result.analysis}</ReactMarkdown>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StrategicTools;
